import React from 'react';
import Styles from './BlogPostPreview.module.less';
import { Link } from '../../router/Link';
import { classes } from '../../../lib';
import { Button } from '../../Button';
import { FontAwesomeIcon } from '../../FontAwesomeIcon';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { generatePath } from 'react-router-dom';
import routes from '../../../pages/landingPage/routes';
import { CategoryLabel } from '../BlogPost/CategoryLabel';
import { DateTime } from 'luxon';
import { CreationDateLabel } from '../BlogPost/CreationDateLabel';
import { ReadingTimeLabel } from '../BlogPost/ReadingTimeLabel';

export type BlogPostPreviewProps = {
    title: string;
    category?: string;
    image: string;
    url: string;
    small?: boolean;
    children?: React.ReactNode;
    color?: string;
    readingTime?: number;
    creationDate?: DateTime;
};

export const Preview = ({
    title,
    category,
    image,
    color,
    creationDate,
    readingTime,
}: BlogPostPreviewProps): JSX.Element => {
    return (
        <div className={classes('flex vertical stretch-top g24 ', Styles.card)}>
            <div className={Styles.image}>
                <div style={{ backgroundImage: `url(${image})` }}>
                    {category && (
                        <CategoryLabel
                            title={category}
                            color={color || 'gray'}
                        />
                    )}
                </div>
            </div>
            <div className="vertical g16 pb16 flex-2">
                <div className="vertical stretch-top g16 px32 medium-alude flex-2">
                    <span className={Styles.title}>{title}</span>
                </div>
                <div
                    className={classes(
                        'horizontal wrap px32 g8',
                        Styles.footer
                    )}>
                    {readingTime && creationDate && (
                        <div className="horizontal left-middle">
                            <span
                                className={classes(
                                    'horizontal',
                                    Styles.creationDateTag
                                )}>
                                <CreationDateLabel
                                    creationDate={creationDate}
                                />
                            </span>
                            <span
                                className={classes(
                                    'horizontal',
                                    Styles.readingTimeTag
                                )}>
                                <ReadingTimeLabel readingTime={readingTime} />
                            </span>
                        </div>
                    )}
                    <Button type="link" size="small">
                        Leia mais <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                </div>
            </div>
        </div>
    );
};

const InternalPost = ({ url, ...props }: BlogPostPreviewProps): JSX.Element => {
    return (
        <Link
            to={generatePath(routes.blog.post.path, { postSlug: url })}
            className={Styles.container}>
            <Preview url={url} {...props} />
        </Link>
    );
};

const ExternalPost = ({ url, ...props }: BlogPostPreviewProps): JSX.Element => {
    return (
        <a
            target="_blank"
            rel="noopener noreferrer"
            href={url}
            className={Styles.container}>
            <Preview url={url} {...props} />
        </a>
    );
};

export const BlogPostPreview = ({
    url,
    ...props
}: BlogPostPreviewProps): JSX.Element => {
    if (url.startsWith('http')) {
        return <ExternalPost url={url} {...props} />;
    }
    return <InternalPost url={url} {...props} />;
};
